import { Button, Panel, SkeletonLoader } from '@postscript/components';
import {
  useApproveProposal,
  useLinkProposal,
  useProposalTargetPreview,
  useRejectProposal,
} from 'components/brand/offers/hooks/useOfferProposals';
import { proposalDescription } from 'components/brand/offers/offerScanPresentation';
import { ProposalVerificationSection } from 'components/brand/offers/ProposalVerification';
import {
  ConflictNote,
  EvidenceQuote,
  EvidenceSource,
  MutedText,
} from 'components/brand/offers/proposalStyles';
import {
  OfferProposal,
  ProposalDescriptionOrigin,
  ProposalDisposition,
  ProposalTargetPreview,
} from 'components/brand/offers/types';
import { ExistingOfferDiff } from 'components/brand/offers/ExistingOfferDiff';
import { ComponentProps, ReactNode } from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import {
  proposalEvidenceKey,
  uniqueProposalEvidence,
} from './proposalEvidence';

const Detail = styled.div`
  display: grid;
  gap: var(--spacing-2);
  padding-top: var(--spacing-2);
  border-top: 1px solid var(--border-color);
`;

const DescriptionText = styled.p`
  margin: 0;
  white-space: pre-line;
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: var(--spacing-2);
  margin: 0;

  div {
    display: grid;
    gap: var(--spacing-1);
  }

  dt {
    color: var(--gray-6);
  }

  dd {
    margin: 0;
  }
`;

const EvidenceList = styled.ul`
  list-style: none;
  display: grid;
  gap: var(--spacing-1);
  margin: 0;
  padding: 0;

  li {
    display: grid;
    gap: var(--spacing-1);
  }
`;

const TargetReference = styled.div`
  display: grid;
  gap: var(--spacing-1);
  color: var(--gray-6);
`;

export const Actions = styled.div`
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
`;

const DescriptionBlock = styled.div`
  display: grid;
  gap: var(--spacing-1);
`;

const ConditionList = styled.ul`
  display: grid;
  gap: var(--spacing-1);
  margin: 0;
  padding-left: var(--spacing-3);
`;

/**
 * Dispositions a reviewer can turn into a draft offer. Creating a draft is
 * the default for every reviewable finding: it never touches a live offer,
 * so it is the safe first choice even when the scanner found a match.
 */
const DRAFTABLE_DISPOSITIONS: ReadonlySet<ProposalDisposition> = new Set([
  ProposalDisposition.NEW,
  ProposalDisposition.ENRICH,
  ProposalDisposition.POSSIBLE_MATCH,
]);

/**
 * How a proposal's existing target is introduced, when it has one. Neither
 * label promises an action: updating or confirming the match is optional,
 * so the line only says which offer the secondary action would touch.
 */
const TARGET_REFERENCE_LABEL: Partial<Record<ProposalDisposition, string>> = {
  [ProposalDisposition.ENRICH]: 'Matches',
  [ProposalDisposition.POSSIBLE_MATCH]: 'Possible match',
};

type ActionButtonProps = ComponentProps<typeof Button>;

/**
 * A row action. The variant is mirrored into the DOM so the hierarchy --
 * which action is the default -- is observable, not just painted.
 */
const ActionButton = ({
  variant,
  ...props
}: ActionButtonProps): JSX.Element => (
  <Button
    type="button"
    size="small"
    variant={variant}
    data-variant={variant}
    {...props}
  />
);

type LabelledValue = [label: string, value: ReactNode];

const withValues = (rows: LabelledValue[]): LabelledValue[] =>
  rows.filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );

const conditionList = (conditions?: string[] | null): ReactNode =>
  conditions?.length ? (
    <ConditionList>
      {conditions.map((condition) => (
        <li key={condition}>{condition}</li>
      ))}
    </ConditionList>
  ) : null;

const factRows = (
  proposal: OfferProposal,
  sightingCount: number,
): LabelledValue[] => {
  const applicability = proposal.consolidation?.applicability;

  return withValues([
    ['Code', proposal.discountCode],
    ['Applies to', applicability?.scope || proposal.scopeAndEligibility],
    ['Eligibility', applicability?.eligibility],
    ['Conditions', conditionList(proposal.conditions)],
    ['Redeem', applicability?.redemption || proposal.redemption],
    ['Timing', applicability?.timing || proposal.timing],
    ['Sightings', sightingCount > 1 ? sightingCount : null],
  ]);
};

interface ProposalContextProps {
  proposal: OfferProposal;
}

/** The trust material: what the scanner disagreed with, and what it read. */
export const ProposalContext = ({
  proposal,
}: ProposalContextProps): JSX.Element => {
  const evidence = uniqueProposalEvidence(proposal.evidence);

  return (
    <>
      {proposal.valueConflict && (
        <ConflictNote>{proposal.valueConflict}</ConflictNote>
      )}

      {proposal.disposition === ProposalDisposition.CONFLICTED && (
        <ConflictNote>
          Pages on your site state competing terms for this promotion, so it
          can&#39;t be approved automatically. Dismiss it, or update your site
          and rescan.
        </ConflictNote>
      )}

      {evidence.length > 0 && (
        <EvidenceList>
          {evidence.map((item) => (
            <li key={proposalEvidenceKey(item)}>
              {item.quote && <EvidenceQuote>“{item.quote}”</EvidenceQuote>}
              {item.sourceUrl && (
                <EvidenceSource
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.sourceUrl}
                </EvidenceSource>
              )}
              {item.observedAtUtc && (
                <MutedText>
                  Captured{' '}
                  {DateTime.fromISO(item.observedAtUtc).toLocaleString({
                    ...DateTime.DATETIME_MED,
                    timeZoneName: 'short',
                  })}
                </MutedText>
              )}
            </li>
          ))}
        </EvidenceList>
      )}
    </>
  );
};

interface TargetOfferReferenceProps {
  proposal: OfferProposal;
  preview?: ProposalTargetPreview;
  isLoading: boolean;
  isError: boolean;
}

export const OfferTargetPreviewPanel = ({
  preview,
  isLoading = false,
  errorMessage,
  onClose,
}: {
  preview?: ProposalTargetPreview;
  isLoading?: boolean;
  errorMessage?: string;
  onClose: () => void;
}): JSX.Element => (
  <Panel
    isOpen
    onCancel={onClose}
    placement="modal"
    width="full"
    style={{ width: 'min(640px, calc(100vw - 48px))' }}
  >
    <Panel.Header onCancel={onClose}>
      {preview?.title || (isLoading ? 'Loading offer' : 'Offer unavailable')}
    </Panel.Header>
    <Panel.Body>
      {isLoading && <SkeletonLoader width="full" height={264} />}
      {errorMessage && <p>{errorMessage}</p>}
      {preview?.unavailableReason && <p>{preview.unavailableReason}</p>}
      {preview && preview.fields.length > 0 && (
        <ExistingOfferDiff
          fields={preview.fields}
          afterLabel={
            preview.action === 'confirm_match'
              ? 'Scanned offer'
              : 'Proposed update'
          }
        />
      )}
    </Panel.Body>
    <Panel.Footer>
      <Button type="button" onClick={onClose}>
        Close
      </Button>
    </Panel.Footer>
  </Panel>
);

/**
 * Which offer the scanner wants to touch, by name rather than by id.
 *
 * Fetch the exact offer. The offers list is paginated, so searching its first
 * page can display the wrong fallback for a valid target.
 */
const TargetOfferReference = ({
  proposal,
  preview,
  isLoading,
  isError,
}: TargetOfferReferenceProps): JSX.Element | null => {
  const label = TARGET_REFERENCE_LABEL[proposal.disposition];

  if (!label) return null;

  return (
    <TargetReference>
      {isLoading && <SkeletonLoader width="full" height={264} />}
      {isError && <span>The matched offer is temporarily unavailable.</span>}
      {preview?.title && (
        <span>
          {label}: {preview.title}
        </span>
      )}
      {preview?.unavailableReason && <span>{preview.unavailableReason}</span>}
    </TargetReference>
  );
};

interface ProposalDetailProps {
  proposal: OfferProposal;
  sightingCount: number;
  showActions?: boolean;
}

export const ProposalDecisionActions = ({
  proposal,
  onDecided,
}: {
  proposal: OfferProposal;
  onDecided?: () => void;
}): JSX.Element => {
  const approve = useApproveProposal();
  const reject = useRejectProposal();
  const link = useLinkProposal();
  const hasTarget = Boolean(
    proposal.targetAiOfferId || proposal.targetOmsOfferUuid,
  );
  const targetQuery = useProposalTargetPreview(
    hasTarget ? proposal.id : null,
    proposal.updatedAt,
  );
  const preview = targetQuery.data;
  const hasCurrentPreview = Boolean(
    targetQuery.isSuccess &&
      !targetQuery.isFetching &&
      !targetQuery.isError &&
      preview?.proposalUpdatedAt === proposal.updatedAt,
  );
  const hasEligibleUpdate = Boolean(
    hasCurrentPreview &&
      preview?.canApply &&
      preview.fields.some(({ changed }) => changed),
  );
  const busy = approve.isLoading || reject.isLoading || link.isLoading;
  const canCreateDraft = DRAFTABLE_DISPOSITIONS.has(proposal.disposition);
  const asNew = proposal.disposition !== ProposalDisposition.NEW;
  const canUpdateOffer = preview
    ? preview.action === 'update_existing'
    : proposal.targetKind === 'ai_offer' &&
      proposal.disposition === ProposalDisposition.ENRICH;
  const canConfirmMatch = preview
    ? preview.action === 'confirm_match'
    : hasTarget &&
      (proposal.targetKind === 'oms_offer' ||
        proposal.disposition === ProposalDisposition.POSSIBLE_MATCH);

  return (
    <Actions>
      {canUpdateOffer && (
        <ActionButton
          variant="primary"
          loading={approve.isLoading}
          disabled={busy || !hasEligibleUpdate}
          onClick={() =>
            approve.mutate(
              {
                id: proposal.id,
                disposition: proposal.disposition,
                expectedProposalUpdatedAt: proposal.updatedAt,
                expectedTargetUpdatedAt: preview?.targetUpdatedAt ?? undefined,
              },
              onDecided ? { onSuccess: onDecided } : undefined,
            )
          }
        >
          Update offer
        </ActionButton>
      )}
      {canCreateDraft && (
        <ActionButton
          variant={canUpdateOffer ? 'secondary' : 'primary'}
          loading={approve.isLoading}
          disabled={busy}
          onClick={() =>
            approve.mutate(
              {
                id: proposal.id,
                ...(asNew && { asNew }),
                disposition: proposal.disposition,
                expectedProposalUpdatedAt: proposal.updatedAt,
              },
              onDecided ? { onSuccess: onDecided } : undefined,
            )
          }
        >
          {asNew ? 'Create separate draft' : 'Create draft offer'}
        </ActionButton>
      )}
      {canConfirmMatch && (
        <ActionButton
          variant="secondary"
          loading={link.isLoading}
          disabled={busy || !hasCurrentPreview || !preview?.canApply}
          onClick={() =>
            link.mutate(
              {
                id: proposal.id,
                targetAiOfferId: preview?.targetAiOfferId ?? undefined,
                targetOmsOfferUuid: preview?.targetOmsOfferUuid ?? undefined,
                expectedProposalUpdatedAt: proposal.updatedAt,
                expectedTargetUpdatedAt: preview?.targetUpdatedAt ?? undefined,
              },
              onDecided ? { onSuccess: onDecided } : undefined,
            )
          }
        >
          Confirm match
        </ActionButton>
      )}
      <ActionButton
        variant="secondary"
        loading={reject.isLoading}
        disabled={busy}
        onClick={() =>
          reject.mutate(
            proposal.id,
            onDecided ? { onSuccess: onDecided } : undefined,
          )
        }
      >
        Dismiss
      </ActionButton>
    </Actions>
  );
};

export const ProposalDetail = ({
  proposal,
  sightingCount,
  showActions = true,
}: ProposalDetailProps): JSX.Element => {
  const hasTarget = Boolean(
    proposal.targetAiOfferId || proposal.targetOmsOfferUuid,
  );
  const targetQuery = useProposalTargetPreview(
    hasTarget ? proposal.id : null,
    proposal.updatedAt,
  );
  const description = proposalDescription(proposal);
  const factsOnly =
    !proposal.consolidatedDescription?.trim() &&
    proposal.descriptionOrigin === ProposalDescriptionOrigin.FACTS_ONLY;
  const isNewDraft = proposal.disposition === ProposalDisposition.NEW;

  return (
    <Detail>
      {(description || isNewDraft) && (
        <DescriptionBlock>
          <strong>Description</strong>
          <DescriptionText>
            {description || 'No description yet'}
          </DescriptionText>
          {description && factsOnly && (
            <MutedText>Generated copy was dropped; facts only.</MutedText>
          )}
        </DescriptionBlock>
      )}

      <TargetOfferReference
        proposal={proposal}
        preview={targetQuery.data}
        isLoading={targetQuery.isLoading && hasTarget}
        isError={targetQuery.isError}
      />

      {(targetQuery.data?.fields.length ?? 0) > 0 && (
        <ExistingOfferDiff
          fields={targetQuery.data?.fields ?? []}
          afterLabel={
            targetQuery.data?.action === 'confirm_match'
              ? 'Scanned offer'
              : 'Proposed update'
          }
        />
      )}

      <Facts>
        {factRows(proposal, sightingCount).map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </Facts>

      <ProposalVerificationSection verification={proposal.verification} />

      <ProposalContext proposal={proposal} />

      {showActions && <ProposalDecisionActions proposal={proposal} />}
    </Detail>
  );
};
